/**
 * Runtime environment detection so the UI can differentiate localhost / dev / app.
 *
 * Detection is by the browser host (works for the deployed builds) plus the local
 * dev-api stack heuristic (works for `npm start`).
 */
import { isLocalDevApiStack } from './baseUrl';

export type AppEnvironmentKey = 'local' | 'dev' | 'app' | 'other';

export type AppEnvironment = {
  /** Machine key for logic. */
  key: AppEnvironmentKey;
  /** Short suffix used for per-environment asset files, e.g. `dev` → `logo.dev.png`. */
  short: string;
  /** Human badge label; empty string means "no badge" (production app). */
  label: string;
  /** Accent color for the badge/tint. */
  color: string;
};

function currentHostname(): string {
  if (typeof window === 'undefined' || !window.location) {
    return '';
  }
  return window.location.hostname.toLowerCase();
}

/** Private/LAN ranges used when testing the dev server from a phone (http://10.x, 192.168.x, …). */
function isPrivateLanHost(host: string): boolean {
  return (
    /^10\./.test(host) ||
    /^192\.168\./.test(host) ||
    /^172\.(1[6-9]|2\d|3[01])\./.test(host)
  );
}

export function resolveAppEnvironment(): AppEnvironment {
  const host = currentHostname();

  if (host === 'app.offizz.ai' || host === 'app-next.offizz.ai') {
    return { key: 'app', short: 'app', label: '', color: '#1f4fb0' };
  }
  if (host === 'dev.offizz.ai') {
    return { key: 'dev', short: 'dev', label: 'DEV', color: '#e0a100' };
  }
  if (
    host === 'localhost' ||
    host === '127.0.0.1' ||
    host === '::1' ||
    isPrivateLanHost(host) ||
    isLocalDevApiStack()
  ) {
    return { key: 'local', short: 'local', label: 'LOCAL', color: '#c93c37' };
  }
  return { key: 'other', short: 'app', label: '', color: '#1f4fb0' };
}

/**
 * Ordered logo sources to try for the current environment: the per-env file first
 * (e.g. `offizz-landing-logo.dev.png`), then the shared base file as a fallback.
 * Drop a matching file into `public/` to use a distinct logo per environment.
 */
export function envLogoCandidates(baseSrc: string): string[] {
  const env = resolveAppEnvironment();
  const dot = baseSrc.lastIndexOf('.');
  if (env.key === 'app' || env.key === 'other' || dot === -1) {
    return [baseSrc];
  }
  const envSrc = `${baseSrc.slice(0, dot)}.${env.short}${baseSrc.slice(dot)}`;
  return [envSrc, baseSrc];
}
